import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';

import { useAppSelector } from '@/app/hook';
import { useGetBotsQuery } from '@/app/services/chatbot-rtk.services';
import usePermission from '@/hooks/usePermission';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';
import Utilities from '@/utils';
import { BotCategoryEnum } from '@/utils/BotCategoryEnum';
import { BotTypeEnum } from '@/utils/BotTypeEnum';

import CustomContainer from '../../../../../components/ui/CustomContainer';
import { selectCurrentDataState, selectCurrentTypeState } from '../../create.slice';
import ChatbotName from '../ChatbotName';
import Banner from './Banner';
import ChatBotCard from './ChatBotCard';
import ChatBotTypeList from './ChatBotTypeList';

function SelectBotType() {
  const { t } = useTranslation();
  const permissions = usePermission();
  const currentData = useAppSelector(selectCurrentDataState);
  const currentType = useAppSelector(selectCurrentTypeState);
  const canCreateMore = Utilities.useCanCreateMoreChatbots();

  const { register, setValue, formState, watch } = useFormContext();

  const { isLoading, isError, isSuccess, refetch, data, isFetching } = useGetBotsQuery();

  const [selected, setSelected] = useState<string | null>(null);
  const [chatBotType, setChatBotType] = useState<BotCategoryEnum>(
    currentType == BotTypeEnum.HUMAN_CLONE ? BotCategoryEnum.AI_BASED_CHATBOT : BotCategoryEnum.SCENARIO_BASED_CHATBOT
  );
  const isRestoredFromData = useRef<boolean>(false);
  const filterChatBots = (type: BotCategoryEnum) => {
    return currentType == BotTypeEnum.HUMAN_CLONE
      ? data?.filter((item: any) => item.code === BotTypeEnum.HUMAN_CLONE && item.type === type) || []
      : data?.filter((item: any) => item.code !== 'KITTY_01' && item.code !== BotTypeEnum.HUMAN_CLONE && item.type === type) || [];
  };

  const hasRequiredPermission = (code: string) => {
    let permission = false;
    if (code == BotTypeEnum.VIDEO_SCENARIO || code == BotTypeEnum.TEXT_SCENARIO) {
      permission = permissions.widgetPage.main.some(dr => dr.includes(code));
    } else if (currentType == BotTypeEnum.HUMAN_CLONE) {
      permission = !permissions?.homePage?.extension?.upgrade;
    } else {
      permission = canCreateMore;
    }
    return permission;
  };

  const handleSelectWidget = (item: any) => {
    setSelected(item.code);
    setValue('bot_code', item.code);
  };

  const handleSelectClone = (id: string) => {
    setValue('bot_code', BotTypeEnum.HUMAN_CLONE);
    setSelected(id);
    if (id !== 'basic') {
      setValue(
        'cloneExtra',
        {
          data:
            currentData?.cloneExtra?.data || id === 'clonePlusDescription'
              ? {
                  bio: '',
                  profession: '',
                  name: '',
                }
              : id === 'clonePlusVideo'
              ? { botId: '' }
              : {},
          template: id,
          externalLinks: [],
        },
        { shouldValidate: true }
      );
    } else {
      setValue(
        'cloneExtra',
        {
          template: id,
          externalLinks: [],
        },
        { shouldValidate: true }
      );
    }
  };

  function selectFirstPermittedChatBot(type: BotCategoryEnum) {
    isRestoredFromData.current = false;
    setChatBotType(type);
  }

  useEffect(() => {
    if (!currentData || Object.keys(currentData).length === 0) return;
    if (currentData?.bot_code) {
      setValue('bot_code', currentData.bot_code);
      setChatBotType(
        currentType == BotTypeEnum.VIDEO_SCENARIO || currentType == BotTypeEnum.TEXT_SCENARIO
          ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
          : BotCategoryEnum.AI_BASED_CHATBOT
      );
      setSelected(currentData?.bot_code !== BotTypeEnum.HUMAN_CLONE ? currentData?.bot_code : currentData?.cloneExtra?.template);
    }
    isRestoredFromData.current = true;
  }, [currentData]);

  useEffect(() => {
    if (!isRestoredFromData.current) {
      const filter = filterChatBots(chatBotType);
      for (const item of filter) {
        if (hasRequiredPermission(item.code)) {
          setTimeout(() => {
            currentType === BotTypeEnum.HUMAN_CLONE ? handleSelectClone(item.templates[0]?.id) : handleSelectWidget(item);
          }, 200);
          break;
        }
      }
    }
  }, [chatBotType, isRestoredFromData]);

  useEffect(() => {
    if (currentType == BotTypeEnum.HUMAN_CLONE) {
      setValue('bot_code', BotTypeEnum.HUMAN_CLONE);
    }
  }, [currentType]);

  const chatBotContent = useCallback(() => {
    return (
      <div className="tw-w-full tw-flex tw-flex-col tw-gap-3 tw-mt-5">
        <ChatBotTypeList
          selected={selected}
          data={filterChatBots(chatBotType)}
          handleSelectClone={handleSelectClone}
          handleSelectWidget={handleSelectWidget}
          hasPermission={canCreateMore as boolean}
        />
      </div>
    );
  }, [data, selected, chatBotType, isSuccess, isFetching]);

  return (
    <div className="bot-engine-selector md:tw-px-8 tw-px-1">
      {currentType !== BotTypeEnum.HUMAN_CLONE && <Banner />}

      {currentType === BotTypeEnum.HUMAN_CLONE && <ChatbotName />}
      {/* Chatbot type cards */}
      <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex tw-mb-5">
        <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-flex">
          <div className="tw-self-stretch tw-text-gray-900 tw-text-2xl tw-font-semibold tw-font-['Lexend'] tw-leading-loose">
            {currentType !== BotTypeEnum.HUMAN_CLONE ? t('Wizard/step1/key3') : t('Wizard/step1/key4')}
          </div>
          <div className="tw-self-stretch tw-text-gray-900 text-sm font-normal tw-font-['Lexend'] tw-leading-tight">
            {currentType !== BotTypeEnum.HUMAN_CLONE ? t('Wizard/step1/key5') : t('Wizard/step1/key6')}
          </div>
        </div>

        <input type="hidden" {...register('bot_code')} />

        <CustomContainer {...{ isError, isLoading, isSuccess, refetch }}>
          <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
            {currentType !== BotTypeEnum.HUMAN_CLONE && (
              <div className="tw-w-full tw-flex tw-flex-col tw-gap-3">
                <div className="tw-w-full tw-flex lg:tw-flex-row tw-flex-col tw-items-center tw-justify-start tw-gap-3">
                  <ChatBotCard
                    selected={chatBotType}
                    title={t('Wizard/step1/scenarioChatbot')}
                    badge={t('Wizard/step1/key2')}
                    type={BotCategoryEnum.SCENARIO_BASED_CHATBOT}
                    onClick={(type: BotCategoryEnum) => {
                      selectFirstPermittedChatBot(type);
                    }}
                    subDescription={ReactHtmlParser(t('DashboardCreatePartial/desc-scenario'))}
                  />
                  <ChatBotCard
                    selected={chatBotType}
                    title={t('Wizard/step1/IAChatbot')}
                    badge={t('Wizard/step1/key1')}
                    type={BotCategoryEnum.AI_BASED_CHATBOT}
                    onClick={(type: BotCategoryEnum) => {
                      selectFirstPermittedChatBot(type);
                    }}
                    subDescription={ReactHtmlParser(t('DashboardCreatePartial/desc-aibased'))}
                  />
                </div>
              </div>
            )}

            {chatBotContent()}
          </div>
        </CustomContainer>
      </div>
    </div>
  );
}

export default WrappedErrorBoundary(SelectBotType);
